/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useBoardFilters } from './useBoardFilters';
import { Tip } from '@/app/Tip';
import { SearchHint } from '@/app/SearchHint';
import { useSearchShortcut } from '@/lib/shortcut';
import { cn } from '@/lib/cn';

/**
 * The board's search.
 *
 * Inline where the row can give it a usable width, and a magnifier that expands
 * where it cannot — **on the same side of the row either way**. The search does
 * not move in with the bell and the avatar just because the window narrowed.
 *
 * **Exactly one input exists.** The first version rendered the box in two
 * wrappers — an inline one and a collapsed one — which put two elements with
 * `id="board-search"` in the document at once. Duplicate ids break
 * `getElementById`, label association, and the selector contract the browser
 * suite holds. One node; its container's classes change instead.
 *
 * Two rules learned the hard way in the vanilla app (plan §7.3):
 *
 *  - The input carries no `min-width` floor. A floor inside a shrinking flex item
 *    does not narrow the row, it overflows it — with a long circle name the box
 *    drew straight on top of New task.
 *  - Closing the collapsed search **clears the query**. A search box that is
 *    hidden while it still holds text is a board that looks empty for no reason
 *    anybody can see.
 *
 * The value is local state synced to the URL rather than read straight from it,
 * so typing does not push a history entry per keystroke.
 */
export function BoardSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { filters, setFilter } = useBoardFilters();
  const [value, setValue] = useState(filters.q);
  const input = useRef<HTMLInputElement>(null);

  // Follow the URL when it changes from outside — a shared link, the back button,
  // or Clear — but never fight what is being typed.
  useEffect(() => {
    setValue((v) => (v === filters.q ? v : filters.q));
  }, [filters.q]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (value !== filters.q) setFilter({ q: value });
    }, 200);
    return () => clearTimeout(id);
  }, [value, filters.q, setFilter]);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  // ⌘K / Ctrl+K puts the cursor in here from anywhere on the board. **The
  // mechanism moved to `lib/shortcut.ts`** so the other three searches — List,
  // Archived, My Day — could have it too; this one keeps the extra step, which
  // is that below `laptop` the box is not on screen and opening it is what the
  // shortcut does there. Nothing else needs that, which is why it is a callback
  // and not part of the hook.
  const openFirst = useCallback(() => {
    if (!open) onOpenChange(true);
  }, [open, onOpenChange]);
  useSearchShortcut(input, openFirst);

  return (
    <>
      <div
        // Both the id and the class: `board-search` measures this box's position
        // against the heading and New task by id, and the vanilla markup carries
        // both too. There is only ever one of these on screen.
        id="board-search-wrap"
        className={cn(
          'board-search-wrap min-w-0 items-center',
          // Inline from `laptop` up — it was `wide`, and the row could not have
          // paid for it any earlier when the mark, the bell and the avatar were
          // still in it. Everyone on a 1280 or 1366 laptop, which is most of the
          // team, got a magnifier where there was room for a search box.
          // Below that, only while open — and then it takes the row, because the
          // alternative is wrapping the brand onto a second line, and a wrapping
          // flex container moves an item instead of shrinking it. That is what
          // put a long circle name on three lines.
          open ? 'flex flex-1' : 'hidden laptop:flex',
          // Fixed at `wide`, not `flex-1 max-w-[250px]`. Sharing the row's slack
          // with the heading let a long circle name squeeze the box to 95px — a
          // control that looks like a search and holds eight characters, which
          // `topbar-responsive` calls out as worse than showing the opener.
          // **The heading is the thing that gives way**, and it can: it has
          // `min-w-0` and an ellipsis and its full text in a title attribute.
          // A floor on the box itself would be §7.3 rule 1 all over again — a
          // shrink floor inside a flex item overflows instead of narrowing.
          //
          // This was cut to 210 for a while: the brand mark had joined this row,
          // the heading was the only thing in it with `shrink`, and so the mark
          // was paid for entirely out of the circle's name — 3px of it at
          // 1440-1600. Both halves of that have since gone. The mark moved to
          // the bar's own row, and the row stopped handing an equal share to a
          // tools group that only wants 123px of it. Measured after: the heading
          // holds 208px at every width from 900 up, with the box back at 250.
          //
          // Two widths, because 1180 is where the box now arrives and 1180
          // cannot afford 250 of it: at that size the row has 529px for the
          // whole board group, and a 250px box would leave 65px of the circle's
          // name — the squeeze this row keeps re-inventing. 180 leaves 135px of
          // name and is still well clear of the 130px `topbar-responsive` calls
          // the floor for a usable box.
          'laptop:w-[180px] laptop:flex-none wide:w-[250px]',
        )}
      >
        <div className="relative flex min-w-0 flex-1 items-center">
          <Search className="pointer-events-none absolute left-2.5 size-4 text-ink-faint" aria-hidden />
          <input
            id="board-search"
            ref={input}
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search this board…"
            autoComplete="off"
            // Filled with the page colour rather than white. The bar behind it
            // is white now, and a white field on a white bar is a hairline
            // rectangle you have to look for; it goes white on focus, so the
            // control still says which state it is in.
            className="h-[34px] w-full min-w-0 rounded-lg border border-border bg-bg pl-8 pr-2.5 text-base transition-colors duration-fast ease-out-soft placeholder:text-ink-faint focus:border-action focus:bg-surface laptop:pr-14"
          />

          {/* The shortcut, where the shortcut is. Shared with the other three
              searches now — see `SearchHint`. */}
          <SearchHint when={!value} />
        </div>
      </div>

      {/* The opener's label, since it never has one on screen. It replaced a
          `title`, which both fires late and never reaches a keyboard. */}
      <Tip label={open ? 'Close search' : 'Search this board'}>
        <button
          id="board-search-btn"
          type="button"
          aria-label="Search this board"
          aria-expanded={open}
          onClick={() => {
            if (open && value) {
              setValue('');
              setFilter({ q: '' });
            }
            onOpenChange(!open);
          }}
          className={cn(
            'tap flex size-[34px] shrink-0 items-center justify-center rounded-lg border transition-colors duration-fast ease-out-soft laptop:hidden',
            open || filters.q
              ? 'border-action bg-action-bg text-action'
              : 'border-border bg-surface text-ink-muted hover:border-border-strong hover:text-ink',
          )}
        >
          <Search className="size-4" aria-hidden />
        </button>
      </Tip>
    </>
  );
}
